import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Copy, Users, Download, Star, EyeOff, Undo2 } from "lucide-react";
import { fmtCurrency } from "@/lib/format";

const csvCell = (v) => {
  let s = v == null ? "" : String(v);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const fmtDate = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

const copyId = async (id) => {
  try { await navigator.clipboard.writeText(id); toast.success(`DrChrono ID ${id} copied`); }
  catch { toast.error("Could not copy"); }
};

function exportCsv(groups) {
  const head = ["Group", "Reason", "Suggested", "First name", "Last name", "DrChrono ID", "Phone", "Email", "Visits", "Last visit", "Lifetime value"];
  const lines = [head.join(",")];
  groups.forEach((g, gi) => g.patients.forEach((p) => lines.push([gi + 1, g.reason, p.keep ? "Keep" : "Review / merge", p.first_name, p.last_name,
    p.drchrono_id, p.phone, p.email, p.visit_count || 0, p.last_visit || "", p.lifetime_value || 0].map(csvCell).join(","))));
  const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: "possible-duplicate-patients.csv" });
  a.click(); URL.revokeObjectURL(url);
}

const PatientRow = ({ p }) => (
  <tr data-testid={`dup-patient-${p.id}`} className={p.keep ? "bg-emerald-50/60" : ""}>
    <td className="px-3 py-2 whitespace-nowrap font-medium text-slate-800">
      {p.keep && <Star className="inline h-3.5 w-3.5 mr-1 text-emerald-600 fill-emerald-600" />}{p.first_name} {p.last_name}
    </td>
    <td className="px-3 py-2 whitespace-nowrap">
      {p.drchrono_id ? (
        <button data-testid={`dup-copy-${p.id}`} onClick={() => copyId(p.drchrono_id)} className="inline-flex items-center gap-1 font-mono text-xs text-[#0B1F44] hover:underline">
          {p.drchrono_id}<Copy className="h-3 w-3" />
        </button>
      ) : <span className="text-xs text-slate-400">CRM only</span>}
    </td>
    <td className="px-3 py-2 whitespace-nowrap text-slate-600">{p.phone || "—"}</td>
    <td className="px-3 py-2 whitespace-nowrap text-slate-600">{p.email || "—"}</td>
    <td className="px-3 py-2 whitespace-nowrap text-slate-600">{p.visit_count || 0} · {fmtDate(p.last_visit)}</td>
    <td className="px-3 py-2 whitespace-nowrap text-slate-600">{fmtCurrency(p.lifetime_value || 0)}</td>
  </tr>
);

export const DuplicatePatients = () => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [showHidden, setShowHidden] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["patient-duplicates"], enabled: open,
    queryFn: async () => (await api.get("/patients/duplicates")).data });
  const toggle = useMutation({
    mutationFn: ({ key, hide }) => api.post(`/patients/duplicates/${hide ? "dismiss" : "restore"}`, { key }),
    onSuccess: (_, { key, hide }) => {
      qc.invalidateQueries({ queryKey: ["patient-duplicates"] });
      if (hide) toast.success("Hidden as not a duplicate", { action: { label: "Undo", onClick: () => toggle.mutate({ key, hide: false }) } });
      else { toast.success("Group is back in the list"); if ((data?.hidden?.length || 0) <= 1) setShowHidden(false); }
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update"),
  });
  const source = showHidden ? data?.hidden || [] : data?.groups || [];
  const groups = source.filter((g) => !filter.trim() || g.name.toLowerCase().includes(filter.trim().toLowerCase()));

  return (
    <>
      <Button data-testid="dup-open-button" variant="outline" size="sm" className="h-8" onClick={() => setOpen(true)}>
        <Users className="h-4 w-4 mr-1.5" />Possible duplicates
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="dup-dialog" className="sm:max-w-6xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading">Possible duplicate patients</DialogTitle>
            <DialogDescription>
              Records with the same name (nicknames count, e.g. Mike and Michael) that share a phone number or email. The starred record has the most visits and billing,
              so it's usually the one to keep. Merge the others into it in DrChrono; the CRM updates on the next sync.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-3">
            <span data-testid="dup-summary" className="text-sm font-semibold text-slate-700">
              {isLoading ? "Checking all patients…" : `${data?.groups.length || 0} groups · ${data?.extra_records || 0} extra records`}
            </span>
            <button data-testid="dup-toggle-hidden" onClick={() => setShowHidden((v) => !v)} disabled={!data?.hidden?.length && !showHidden}
              className="text-xs font-medium text-[#0B1F44] hover:underline disabled:text-slate-300 disabled:no-underline">
              {showHidden ? "Back to the list" : `Show hidden (${data?.hidden?.length || 0})`}
            </button>
            <Input data-testid="dup-filter" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by name" className="h-8 w-56 ml-auto" />
            <Button data-testid="dup-export" size="sm" variant="outline" className="h-8" disabled={!groups.length} onClick={() => exportCsv(groups)}>
              <Download className="h-4 w-4 mr-1.5" />Export CSV
            </Button>
          </div>
          <div className="overflow-y-auto space-y-4 pr-1">
            {!isLoading && groups.length === 0 && <p data-testid="dup-empty" className="text-sm text-slate-500 py-8 text-center">{showHidden ? "No hidden groups." : "No possible duplicates found."}</p>}
            {groups.map((g, i) => (
              <div key={g.key} data-testid={`dup-group-${i}`} className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-slate-50 text-sm flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-slate-800">{g.name}</span>
                  <span className="text-xs text-slate-500">{g.patients.length} records · {g.reason}</span>
                  {g.hidden_by && <span className="text-xs text-slate-400">Hidden by {g.hidden_by} · {fmtDate(g.hidden_at)}</span>}
                  <Button data-testid={`dup-${g.hidden_by ? "restore" : "dismiss"}-${i}`} size="sm" variant="outline" className="h-7 text-xs ml-auto"
                    disabled={toggle.isPending} onClick={() => toggle.mutate({ key: g.key, hide: !g.hidden_by })}>
                    {g.hidden_by ? <><Undo2 className="h-3.5 w-3.5 mr-1" />Show again</> : <><EyeOff className="h-3.5 w-3.5 mr-1" />Not a duplicate</>}
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[760px] text-sm">
                    <thead className="text-[11px] uppercase tracking-wide text-slate-400 text-left">
                      <tr><th className="px-3 py-1.5">Name</th><th className="px-3 py-1.5">DrChrono ID</th><th className="px-3 py-1.5">Phone</th>
                        <th className="px-3 py-1.5">Email</th><th className="px-3 py-1.5">Visits · last</th><th className="px-3 py-1.5">Billed</th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">{g.patients.map((p) => <PatientRow key={p.id} p={p} />)}</tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
